// Libraries
import React from 'react';

// Supermove
import {PhoneInput, Space, Styled} from '@supermove/components';
import {gql} from '@supermove/graphql';
import {DrawerType, useDrawer, useToast} from '@supermove/hooks';
import {ProjectModel, UserModel} from '@supermove/models';
import {colors} from '@supermove/styles';

// App
import DrawerWithAction from '@shared/design/components/Drawer/DrawerWithAction';
import FieldInput from '@shared/design/components/Field/FieldInput';
import Text from '@shared/design/components/Text';
import SuccessToast from '@shared/design/components/Toast/SuccessToast';
import ProjectClientsForm from '@shared/modules/Project/forms/ProjectClientsForm';
import useUpdateProjectClientsMutation from '@shared/modules/Project/hooks/useUpdateProjectClientsMutation';

type FocusField = 'email' | 'phoneNumber';

const Container = Styled.View`
    justify-content: center;
    align-items: center;
    height: 156px;
    padding: 16px;
`;

const DrawerContainer = Styled.View`
`;

const ContactInfoDrawer = ({
  drawer,
  project,
  handleSuccess,
  focusField,
  user,
}: {
  drawer: DrawerType;
  project: ProjectModel;
  handleSuccess: () => void;
  focusField: FocusField;
  user: UserModel;
}) => {
  const successToast = useToast({
    ToastComponent: SuccessToast,
    message: 'Client Information updated!',
  });

  const field = 'projectClientsForm.clientForm.primaryContactForm';

  const projectClientsForm = ProjectClientsForm.edit(project);
  const {form, handleSubmit, submitting} = useUpdateProjectClientsMutation({
    projectClientsForm,
    onSuccess: () => {
      drawer.handleClose();
      successToast.handleToast();
      handleSuccess();
    },
    onError: (error) => console.log({error}),
  });

  return (
    <DrawerWithAction
      isOpen={drawer.isOpen}
      handleClose={drawer.handleClose}
      headerText={'Edit Contact'}
      isSubmitting={submitting}
      handleSubmit={handleSubmit}
      primaryActionText={'Save'}
    >
      <DrawerContainer>
        <Text.Label style={{color: colors.gray.secondary}}>Contact Name</Text.Label>
        <Space height={4} />
        <Text.Body>{user.fullName}</Text.Body>
        <Space height={16} />
        <FieldInput
          {...form}
          name={`${field}.email`}
          label={'Email Address'}
          handleEnter={handleSubmit}
          input={{
            placeholder: 'Enter email',
            autoFocus: focusField === 'email',
          }}
          isResponsive
        />
        <Space height={16} />
        <FieldInput
          {...form}
          name={`${field}.phoneNumber`}
          label={'Phone Number'}
          component={PhoneInput}
          input={{
            placeholder: 'Enter phone number',
            setFieldValue: form.setFieldValue,
            autoFocus: focusField === 'phoneNumber',
          }}
          isResponsive
        />
      </DrawerContainer>
    </DrawerWithAction>
  );
};

interface MissingCommunicationInfoProps {
  linkText: string;
  text: string;
  /** When a drawer to add contact info opens, which field should be focused by default */
  focusField: FocusField;
  onSuccess: () => void;
  project: ProjectModel;
  user: UserModel;
}

const MissingCommunicationInfo = ({
  linkText,
  text,
  focusField,
  onSuccess,
  project,
  user,
}: MissingCommunicationInfoProps) => {
  const contactInfoDrawer = useDrawer({name: 'AddContactInfoDrawer'});
  const handleLinkPress = () => {
    contactInfoDrawer.handleToggle();
  };

  return (
    <Container>
      <Text.Body style={{textAlign: 'center', color: colors.gray.tertiary}}>
        <Text.Link onPress={handleLinkPress}>{linkText}</Text.Link> {text}
      </Text.Body>
      <ContactInfoDrawer
        drawer={contactInfoDrawer}
        project={project}
        handleSuccess={onSuccess}
        focusField={focusField}
        user={user}
      />
    </Container>
  );
};

// --------------------------------------------------
// Data
// --------------------------------------------------
MissingCommunicationInfo.fragment = gql`
  ${ProjectClientsForm.edit.fragment}

  fragment MissingCommunicationInfo on Project {
    id
    ...ProjectClientsForm_edit
  }

  fragment MissingCommunicationInfo_User on User {
    id
    fullName
    email
    phoneNumber
  }
`;

export default MissingCommunicationInfo;
